/**
 * production canonical の dynamic-open-baseline evaluator (ADR 0003 / ADR 0004 解消後の正規ロジック)。
 * rolling 下位 / 上位 percentile (= 直近 baselineWindowMs の raw 2D MAR の p`percentileLow` / p`percentileHigh`)
 * を closed / open baseline として動的算出し、 0..1 にレンジ正規化された effectiveMar を出す pure helper
 * (F29 採用本命 `p29-r5-sustain3000` の R5 base、 F32 本番反映で production canonical 化、
 * docs/eval-optimization-progress.md §F29)。
 *
 * F32 trade-off 受容判断 (ADR 0003 §判断): 頭部姿勢依存性 (= F19 観察、 trial 内の roll/pitch で raw MAR が
 * 1.06-1.47 倍に飽和する trial が存在) を動的 baseline で吸収。 sustainMs=3000 の新 ground truth (issue
 * #188) で `dynamic-open-baseline` + sustainMs spec の組合せが misdet 攻撃 trial の effectiveMar 飽和を
 * 3 秒持続できない構造的優位を持つ (F29 結論)。
 *
 * issue#230 #1a (時間ベース化 + self-erase 回避): 窓を frame 数固定 (`windowFrames=90`) から
 * wall-clock ms (`baselineWindowMs`) に置換し、 実 fps 非依存にした。 `push(mar, timestamp)` で
 * 時刻 evict (`buffer[0].t < newest.t − baselineWindowMs`)、 cold start も coldStartMs で判定する。
 *
 * **self-erase 回避 (必須要件)**: `normalizedMar = (cur − p10) / (p90 − p10)` は開口継続で窓が開
 * フレームで埋まると p10 が開値まで上がり 0 に崩壊する。 default `baselineWindowMs=8000` を
 * `sustainMs=3000` より大きく取ることで、 3 秒 sustain の間 窓に必ず一部 closed history が残り、
 * p10 が崩壊する前に detection-machine が発火する (= 高 fps での取りこぼし回避、 issue#230 §必須要件 1)。
 * この不変条件は detection-loop.test.ts で `DEFAULT_BASELINE_WINDOW_MS > DEFAULT_TUNING.sustainMs`
 * として保証する (型では実行時数値の大小を保証できないため test 保証)。
 *
 * Logic:
 *   1. ring buffer (時刻 evict、 span = baselineWindowMs) に raw 2D MAR を timestamp 付きで push
 *      (face-loss / NaN は skip)
 *   2. 最初の観測から coldStartMs 未満なら判定保留 (= effectiveMar = 0、 発火させない)
 *   3. 蓄積後: sorted nearest-rank で p(percentileLow) / p(percentileHigh) を取り
 *      `effectiveMar = clamp((currentMar - dynamicClosed) / (dynamicOpen - dynamicClosed), 0, 1)`
 *   4. 分母は MIN_OPEN_RANGE (= 製品の最小通知可能開口レンジ floor、 ADR 0008) で下限を設ける。
 *      idle (history がほぼ平坦) は floor が分母を支配し、 微小 drift は threshold 未満に抑えられ黙る /
 *      本物の開口は floor を超えて発火する。 平常運転では `p90 − p10 ≫ floor` で floor は不活性
 *
 * detection-machine の signature は不変。 本 helper の正規化出力が effectiveMar (= issue #302 で
 * 後段の `applyMar3dRatio` deflate を live 経路から除去、 §F53 / ADR 0009 §Status。 旧 2 段目だった)。
 *
 * 既定値の根拠 (F25 / F29 sweep best を ms 換算、 #1b joint sweep + issue#238 F41 で確定):
 *   - `baselineWindowMs = 8000`: **必須 > sustainMs (3000)** で self-erase 回避 (上記)。 #1b sweep で
 *     b5000-b8000 全 cell の TP retention 健全を確認。 issue#238 F41 で 6000→8000 に変更: F40 切り分けで
 *     TP-oblique retention (15→18) は VAD (medfilt) ではなく baseline 窓側が駆動すると確定し、 b6000 では
 *     oblique 退行 (native 15) が残るため省略不可。 self-erase 条件 `(1−10/100)×8000=7200 > 3000` で安全
 *   - `percentileLow = 10`: 下位 10%。 R4 sweep best `p25-dyn-closed-p10-win90` から踏襲、
 *     history の閉口側を低めに張り open scale を保守的に大きく取る
 *   - `percentileHigh = 90`: 上位 10%。 個別 trial の最大開口に追従しつつ outlier 1-2 frame には
 *     引っ張られない帯
 *   - `coldStartMs = 1000`: cold start handling (旧 coldStartFrames=30 @30fps の換算)
 *
 * #1b sweep 結論 (docs/eval-optimization-progress.md §F35 / §F40 / §F41): baselineWindowMs=8000 は fps
 * 横断で TP retention を robust 化 (b6000 は fps 10/8 で 24/25 に低下) し、 vgap-2d-medfilt VAD と組んで
 * TP-oblique を 18 に戻す。 F35 で観測された「b8000 単独は oblique を戻すが misdet FP +6」は medfilt VAD
 * 併用で逆転し misdet FP −12 (主因 smile FP 8→1)。
 */

/** self-erase 回避の不変条件 (baselineWindowMs > sustainMs) を test / assertion で参照するため export する。 */
export const DEFAULT_BASELINE_WINDOW_MS = 8_000;
/** self-erase 崩壊点の算出に使う下位 percentile。 assertion / test 用に export する。 */
export const DEFAULT_PERCENTILE_LOW = 10;
const DEFAULT_PERCENTILE_HIGH = 90;
const DEFAULT_COLD_START_MS = 1_000;
/**
 * ADR 0010 凍結ゲートの開口マージン (raw MAR 単位)。 `mar > p10 + DEFAULT_OPEN_MARGIN` で「開口中」 とみなし
 * 窓を凍結する。 cycle-f44 OPEN_MARGIN sweep (ADR 0010 §帰結) で 0.10 を確定 (= clean 帯 0.08-0.12 の中央)。
 * 0.08-0.12 帯は posture FP 非増加 (face-orientation/out-of-spec/idle-oblique clean、 TP-oblique +1)、 corpus 上は
 * 0.08/0.10/0.12 が同値 (境界に入る trial 無し)。 margin を 0.05/0.03 へ下げると idle / face-orientation FP を
 * 足すため clean 帯内に採る。 test / eval twin から参照するため export する。
 */
export const DEFAULT_OPEN_MARGIN = 0.1;
/**
 * idle (閉口静止) 崩壊ガード兼「製品の最小通知可能開口レンジ」 (ADR 0008 案1 / F42 floor=0.060 暫定確定)。
 *
 * 旧 `MIN_OPEN_OFFSET = 1e-4` は意味=単なる div0 ガードだった。 idle で窓 8 秒が全て閉口値で埋まると
 * `p90 ≈ p10` に潰れ、 分母が 1e-4 で打ち止め → `(微小正値)/1e-4` が 1.0 飽和し sustainMs=3000 を満たして
 * 誤発火する (= 本番 idle FP、 issue#248)。 floor を「通知に値する最小の開口度」 という製品定義の絶対閾
 * (0.060) に引き上げると、 idle 微小 drift の effectiveMar が threshold(0.3) 未満に抑えられ黙る。
 *
 * **平常運転では不活性**: 直近に本物の開口があれば `p90 − p10 ≫ MIN_OPEN_RANGE` で floor は触れず dynamic
 * 正規化がそのまま効く (= データがあれば per-user dynamic / 崩壊したら population 定数 floor の二段構え)。
 * **キャリブレーション復活ではない**: floor は `threshold` / `sustainMs` と同列の population 定数 (UI なし /
 * per-user 測定なし)。 per-user の閉口/開口値は引き続き rolling p10/p90 が自動追従する。
 * 値の根拠は docs/eval-optimization-progress.md §F42 (cycle-2 200 trial + idle 4 trial の全 fps sweep)。
 * 暫定値: TP/oblique retention の完全回復は別軸 (TP-oblique 改善) で取り戻す前提のため ADR 0008 は Proposed。
 * test / eval twin の同値性検証から参照するため export する (= `DEFAULT_BASELINE_WINDOW_MS` 等と同じ理由)。
 */
export const MIN_OPEN_RANGE = 0.06;

export type DynamicOpenBaselineOptions = {
  baselineWindowMs?: number;
  percentileLow?: number;
  percentileHigh?: number;
  coldStartMs?: number;
  /**
   * ADR 0010 (fix-A / A2 unbounded): 開口中 (= raw MAR が現在の p10 + `openMargin` を超える) は baseline
   * 窓への push を凍結し、 持続開口 self-erase (中盤中断崩壊) を構造的に防ぐ。 default `false` で既存挙動
   * (= 開口も窓に積み p10 が崩壊し得る)。 production 化は eval evidence (posture FP 検証) 後 (ADR 0010 Proposed)。
   */
  freezeWhileOpen?: boolean;
  /** 凍結ゲートの開口マージン (raw MAR 単位、 `mar > p10 + openMargin` で凍結)。 sweep placeholder。 */
  openMargin?: number;
};

export type DynamicOpenBaselineEvaluator = {
  /**
   * raw 2D MAR を timestamp 付きで ring buffer に push。 undefined / non-finite な mar / timestamp
   * (= face-loss) は skip。 push 後に `buffer[0].t < newest.t − baselineWindowMs` を時刻 evict する。
   */
  push: (mar: number | undefined, timestamp: number) => void;
  /**
   * 現在の ring buffer から動的 baseline を算出し、 currentMar を 0..1 にレンジ正規化した
   * effectiveMar を返す。 cold start 中 (最初の観測から coldStartMs 未満) は 0 (= 判定保留)。
   */
  evaluate: (currentMar: number) => number;
  /**
   * pose-reset lever (issue #286 / ADR 0013): 窓を全破棄し cold-start を再アームする。
   * 頭部 yaw の大変化 (pose-reset-monitor の trigger) で呼ぶ — 旋回前の姿勢で形成された p10/p90 は
   * 旋回後の MAR 分布に対して系統誤差 (遷移 idle-oblique FP の +0.3 級下駄) になるため、 残存
   * `(1−p10%)×窓` ≈ 7.2s を待たず即座に捨てる。 `firstSeenAt` は通常 evict されないフィールドで、
   * reset だけが明示更新する (= 以後 coldStartMs は reset 時刻起点)。 cold-start 中は evaluate=0
   * (発火保留 = 安全側) なので、 reset が self-erase 型の誤発火経路を作ることはない。
   */
  reset: (timestamp: number) => void;
};

type Sample = { mar: number; t: number };

/**
 * nearest-rank で下位 `percentile`% の値を返す (= evaluate と同 flavor)。 ADR 0010 凍結ゲートが
 * push 時点の p10 を参照するために使う。 空配列は 0。
 */
function lowerPercentile(samples: readonly Sample[], percentile: number): number {
  const sorted = samples.map((s) => s.mar).sort((a, b) => a - b);
  const idx = Math.max(0, Math.min(sorted.length - 1, Math.ceil((percentile / 100) * sorted.length) - 1));
  return sorted[idx] ?? 0;
}

export function createDynamicOpenBaselineEvaluator(
  opts: DynamicOpenBaselineOptions = {},
): DynamicOpenBaselineEvaluator {
  const baselineWindowMs = opts.baselineWindowMs ?? DEFAULT_BASELINE_WINDOW_MS;
  const percentileLow = opts.percentileLow ?? DEFAULT_PERCENTILE_LOW;
  const percentileHigh = opts.percentileHigh ?? DEFAULT_PERCENTILE_HIGH;
  const coldStartMs = opts.coldStartMs ?? DEFAULT_COLD_START_MS;
  const freezeWhileOpen = opts.freezeWhileOpen ?? false;
  const openMargin = opts.openMargin ?? DEFAULT_OPEN_MARGIN;

  // ring buffer (時刻 evict、 span = baselineWindowMs)。
  const samples: Sample[] = [];
  // cold start 判定用の最初の有効 push 時刻 (時刻 evict されない。 vision-vad と同じ理由)。
  let firstSeenAt: number | null = null;

  return {
    push: (mar, timestamp) => {
      if (typeof mar !== "number" || !Number.isFinite(mar)) return;
      if (!Number.isFinite(timestamp)) return;
      if (firstSeenAt === null) firstSeenAt = timestamp;
      // ADR 0010 (fix-A / A2 unbounded): 開口中は窓を凍結し持続開口 self-erase (中盤中断崩壊) を防ぐ。
      // cold-start 経過後、 現在の p10 (= dynamicClosed) + openMargin を超える raw MAR は「開口中」 とみなし
      // 窓に積まない (= p10 が開値に崩壊するデッドライン自体を消す)。 ゲートは raw MAR 基準で
      // detection-machine の openSince と独立 = VAD 誤検知で openSince が消えても凍結を維持する。
      // raw MAR が p10+openMargin を下回る (= 実際に口を閉じる) と push 再開し p10 が回復する (= 主訴の
      // 「閉じれば正常化」)。 default freezeWhileOpen=false で既存挙動 (ADR 0010 Proposed)。
      if (
        freezeWhileOpen &&
        samples.length > 0 &&
        timestamp - firstSeenAt >= coldStartMs &&
        mar > lowerPercentile(samples, percentileLow) + openMargin
      ) {
        return;
      }
      samples.push({ mar, t: timestamp });
      const cutoff = timestamp - baselineWindowMs;
      while (samples.length > 0) {
        const head = samples[0];
        if (head === undefined || head.t >= cutoff) break;
        samples.shift();
      }
    },
    evaluate: (currentMar) => {
      if (!Number.isFinite(currentMar)) return 0;
      const newest = samples[samples.length - 1];
      if (newest === undefined || firstSeenAt === null) return 0;
      // cold start: 最初の観測から coldStartMs 未満は判定保留 (buffer span ではなく first-seen
      // からの経過で見る。 時刻 evict で span は baselineWindowMs 頭打ちのため)。
      if (newest.t - firstSeenAt < coldStartMs) return 0;

      // nearest-rank percentile (= eval substrate `dynamic-open-baseline-v1` と同 flavor)。
      const sorted = samples.map((s) => s.mar).sort((a, b) => a - b);
      const lowIdx = Math.max(
        0,
        Math.min(sorted.length - 1, Math.ceil((percentileLow / 100) * sorted.length) - 1),
      );
      const highIdx = Math.max(
        0,
        Math.min(sorted.length - 1, Math.ceil((percentileHigh / 100) * sorted.length) - 1),
      );
      const dynamicClosed = sorted[lowIdx] ?? 0;
      const dynamicOpen = sorted[highIdx] ?? 0;

      // idle 崩壊ガード (ADR 0008): history がほぼ平坦のとき dynamicOpen ≈ dynamicClosed となり、
      // MIN_OPEN_RANGE (= 製品の最小通知可能開口レンジ 0.060) が分母を支配する。 idle 微小 drift は
      // effectiveMar が threshold 未満に抑えられ黙り、 本物の開口 (cur − p10 ≳ floor) は floor を超えて
      // 発火する。 平常運転では `p90 − p10 ≫ MIN_OPEN_RANGE` で floor は不活性。 NaN は出ない。
      const denom = Math.max(MIN_OPEN_RANGE, dynamicOpen - dynamicClosed);
      const corrected = (currentMar - dynamicClosed) / denom;
      return Math.max(0, Math.min(1, corrected));
    },
    reset: (timestamp) => {
      if (!Number.isFinite(timestamp)) return;
      samples.length = 0;
      firstSeenAt = timestamp;
    },
  };
}

/**
 * self-erase 回避の不変条件を起動時に機械検証する (issue#230 #1b / #236 advisory)。
 *
 * self-erase: `normalizedMar = (cur − p_low) / (p_high − p_low)` は開口継続で窓が開フレームで埋まると
 * p_low (= 下位 `percentileLow`%) が開値まで上がり normalizedMar が 0 に崩壊する。 崩壊は窓の
 * `(1 − percentileLow/100)` 割合 (= percentileLow=10 なら 90%) が開で埋まった時点。 開区間が sustainMs
 * 持続する前に崩壊すると発火不能になるため、 連続開で崩壊閾割合に達するまでの時間
 * `(1 − percentileLow/100) × baselineWindowMs` が sustainMs を上回る必要がある。
 *
 * default (baselineWindowMs=8000 / sustainMs=3000 / percentileLow=10) は 0.9×8000=7200ms > 3000ms と
 * 余裕があるが、 将来 baselineWindowMs を下げる / sustainMs を上げる変更で不変条件が崩れるのを
 * fail-fast する regression guard。 plain Error (= developer config invariant、 dispatcher の
 * chain-order violation と同型。 production では発火しない前提)。
 */
export function assertSelfEraseSafe(
  baselineWindowMs: number,
  sustainMs: number,
  percentileLow: number = DEFAULT_PERCENTILE_LOW,
): void {
  const collapseFillFraction = 1 - percentileLow / 100;
  const timeToCollapseMs = collapseFillFraction * baselineWindowMs;
  if (!(timeToCollapseMs > sustainMs)) {
    throw new Error(
      `[dynamic-open-baseline] self-erase invariant violated: ` +
        `(1 - percentileLow/100) × baselineWindowMs = ${collapseFillFraction} × ${baselineWindowMs} = ${timeToCollapseMs}ms ` +
        `must exceed sustainMs (${sustainMs}ms)。 連続開で normalizedMar が崩壊する前に sustain 到達できず発火不能になる。 ` +
        `baselineWindowMs を上げるか sustainMs を下げること。`,
    );
  }
}
